import { NyttFärgTema, type IFärgKombo } from 'src/theme/NyttFärgtema';

/**
 * Presentationsvärldens konstanter.
 *
 * REGEL SOM ALDRIG FÅR BRYTAS: ingen information får bäras enbart av rörelse.
 * Varje scen måste vara fullständig och läsbar när springsen står stilla — det är
 * det som gör den reducerade vägen ärlig i stället för degraderad. Se RörelseKontext.
 *
 * FÄRGREGEL: kombinationernas mörka halva (primary) är ENBART ljuskälla — glöd,
 * gradient, fyllnad bakom. Den ljusa halvan (secondary) är den enda som får bära
 * text. #20369e mot nästan svart är oläsligt; #9ea9e0 är det inte. Använd ljus()
 * och djup() i stället för att plocka .primary/.secondary för hand.
 */

export const KOMBO = {
    blå: NyttFärgTema.Kombinationer[0],   // #20369e / #9ea9e0
    lila: NyttFärgTema.Kombinationer[1],  // #7c2aa7 / #cf97db
    grön: NyttFärgTema.Kombinationer[2]   // #008662 / #71d4be
} as const;

export type Kombonamn = keyof typeof KOMBO;

export function tillKombo(k: Kombonamn | IFärgKombo): IFärgKombo {
    return typeof k === 'string' ? KOMBO[k] : k;
}

/** Läsbar accent på mörk grund. Den enda färgen som får sättas som `color`. */
export function ljus(k: Kombonamn | IFärgKombo): string {
    return tillKombo(k).secondary.hexkod;
}

/** Ljuskälla. Glöd, gradient, fyllnad — aldrig text. */
export function djup(k: Kombonamn | IFärgKombo): string {
    return tillKombo(k).primary.hexkod;
}

/** Grundvärlden. Mörk och sval, medvetet inte appens ljusa MUI-yta. */
export const GRUND = {
    botten: '#0B0D14',
    upphöjd: '#12151F',
    kant: 'rgba(242, 243, 247, 0.10)',
    text: '#F2F3F7',
    /** Tonad ur grundens kyla, aldrig neutralgrå. ~8:1 mot botten. */
    dämpad: '#A8AEC4'
} as const;

export const SPRING = {
    mjuk: { tension: 170, friction: 26 },
    snärtig: { tension: 320, friction: 30 },
    trög: { tension: 80, friction: 40 },
    studs: { tension: 260, friction: 14 }
} as const;

/** Glödande ljuskälla — radiell, tonar ut mot noll så den kan lagras. */
export function glöd(k: Kombonamn | IFärgKombo, styrka = 0.55): string {
    const f = djup(k);
    return `radial-gradient(circle at 50% 50%, ${hexTillRgba(f, styrka)} 0%, ${hexTillRgba(f, styrka * 0.35)} 38%, ${hexTillRgba(f, 0)} 72%)`;
}

export function gradient(k: Kombonamn | IFärgKombo, vinkel = 135): string {
    const kombo = tillKombo(k);
    return `linear-gradient(${vinkel}deg, ${kombo.primary.hexkod} 0%, ${kombo.secondary.hexkod} 100%)`;
}

/** Två kombinationer blandade. Styrelsen går blå → lila. */
export function tvåtonsGradient(a: Kombonamn | IFärgKombo, b: Kombonamn | IFärgKombo, vinkel = 135): string {
    return `linear-gradient(${vinkel}deg, ${djup(a)} 0%, ${ljus(a)} 40%, ${djup(b)} 70%, ${ljus(b)} 100%)`;
}

export function hexTillRgba(hex: string, alfa: number): string {
    const h = hex.replace('#', '');
    const heltal = parseInt(h.length === 3 ? h.split('').map((t) => t + t).join('') : h, 16);
    return `rgba(${(heltal >> 16) & 255}, ${(heltal >> 8) & 255}, ${heltal & 255}, ${alfa})`;
}

/** Displayfamiljen. Public Sans är appens brödtext och behålls som tråd tillbaka till produkten. */
export const TYPSNITT = {
    display: "'Fraunces Variable', 'Fraunces', Georgia, serif",
    bröd: "'Public Sans Variable', 'Public Sans', system-ui, sans-serif"
} as const;
